import type { ComponentType } from 'react'
import { type AccommodationDetail } from '@/api/accommodation/types'
import { Typography } from '@/components/ui/typography'
import {
  Bath,
  Beef,
  Flame,
  FlameKindling,
  Heart,
  Mountain,
  PartyPopper,
  PawPrint,
  Snowflake,
  SquareParking,
  Sun,
  ThermometerSun,
  ToyBrick,
  TreePine,
  Trees,
  Users,
  Waves,
} from 'lucide-react'

interface Highlight {
  icon: ComponentType<{ className?: string }>
  title: string
  description: string
}

// Only the curated stay-type labels (samota v přírodě, romantika pro dva,
// pro rodinu, pro partu, krásný výhled, hory a příroda, u vody) are shown;
// any other label from the API is dropped.
function labelHighlight(name: string): Highlight | null {
  const normalized = name.toLowerCase()
  if (normalized.includes('samot')) {
    return {
      icon: Trees,
      title: name,
      description: 'Klid daleko od civilizace.',
    }
  }
  if (normalized.includes('romantik')) {
    return {
      icon: Heart,
      title: name,
      description: 'Klid a soukromí pro dva.',
    }
  }
  if (normalized.includes('rodin')) {
    return {
      icon: Users,
      title: name,
      description: 'Zázemí pro děti i dospělé.',
    }
  }
  if (normalized.includes('part')) {
    return {
      icon: PartyPopper,
      title: name,
      description: 'Dost místa pro celou partu.',
    }
  }
  if (normalized.includes('výhled')) {
    return {
      icon: Mountain,
      title: name,
      description: 'Výhled, který neomrzí.',
    }
  }
  if (normalized.includes('hor') || normalized.includes('přírod')) {
    return {
      icon: TreePine,
      title: name,
      description: 'Příroda hned za dveřmi.',
    }
  }
  if (normalized.includes('vod')) {
    return {
      icon: Waves,
      title: name,
      description: 'Voda na dosah.',
    }
  }
  return null
}

// Ordered by how strongly each perk sells a last-minute stay; the section
// renders the first four that the location actually has.
function buildHighlights(data: AccommodationDetail): Highlight[] {
  const { facility, conditions, unit } = data
  const candidates: Array<[boolean, Highlight]> = [
    [
      facility.sauna,
      {
        icon: ThermometerSun,
        title: 'Sauna',
        description: 'Prohřejete se po celodenním výletě.',
      },
    ],
    [
      facility.whirlpool,
      {
        icon: Waves,
        title: 'Vířivka',
        description: 'Horká lázeň jen pro vás.',
      },
    ],
    [
      facility.hotTub,
      {
        icon: Bath,
        title: 'Koupací sud',
        description: 'Horká koupel pod širým nebem.',
      },
    ],
    [
      facility.pool,
      {
        icon: Waves,
        title: 'Bazén',
        description: 'Osvěžení přímo u objektu.',
      },
    ],
    [
      unit.facility.general.indoorFireplace,
      {
        icon: Flame,
        title: 'Krb',
        description: 'Večery u živého ohně.',
      },
    ],
    [
      unit.facility.general.airConditioning,
      {
        icon: Snowflake,
        title: 'Klimatizace',
        description: 'V létě příjemný chládek.',
      },
    ],
    [
      conditions.pets,
      {
        icon: PawPrint,
        title: 'Mazlíčci vítáni',
        description: 'Váš pes jede s vámi.',
      },
    ],
    [
      facility.grill,
      {
        icon: Beef,
        title: 'Gril',
        description: 'Grilovačka za letních večerů.',
      },
    ],
    [
      facility.outdoorFireplace,
      {
        icon: FlameKindling,
        title: 'Ohniště',
        description: 'Večerní posezení u ohně.',
      },
    ],
    [
      facility.childrenPlayground,
      {
        icon: ToyBrick,
        title: 'Dětské hřiště',
        description: 'Děti se zabaví samy.',
      },
    ],
    [
      facility.terrace,
      {
        icon: Sun,
        title: 'Terasa',
        description: 'Ranní káva s výhledem.',
      },
    ],
    [
      facility.freeParking,
      {
        icon: SquareParking,
        title: 'Parkování zdarma',
        description: 'Auto necháte přímo u objektu.',
      },
    ],
  ]

  const facilityHighlights = candidates.filter(([has]) => has).map(([, highlight]) => highlight)

  // Stay-type labels lead (they say who the place is for), facilities follow.
  const labelHighlights = (data.labels ?? [])
    .map((label) => labelHighlight(label.name))
    .filter((highlight): highlight is Highlight => highlight !== null)

  return [...labelHighlights, ...facilityHighlights].slice(0, 6)
}

interface DetailHighlightsSectionProps {
  data: AccommodationDetail
}

export function DetailHighlightsSection({ data }: DetailHighlightsSectionProps) {
  const highlights = buildHighlights(data)
  if (highlights.length < 2) return null

  return (
    <div>
      <Typography variant="h2" className="mb-6">
        Proč se vám tu bude líbit
      </Typography>
      <div className="grid gap-3 sm:grid-cols-2">
        {highlights.map((highlight) => (
          <div key={highlight.title} className="flex items-center gap-3 rounded-xl border border-slate-200 p-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-red-50">
              <highlight.icon className="text-red h-5 w-5" />
            </div>
            <div>
              <div className="font-medium text-slate-900">{highlight.title}</div>
              {highlight.description && <div className="text-sm text-slate-500">{highlight.description}</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
